import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import BottomFixed from "@/components/bottom-fixed";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Bookmark,
  CirclePlus,
  Copy,
  Ellipsis,
  Loader,
  Save,
  Undo,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { handleApiError } from "@/utils/errors";
import { dilemmasRepo } from "@/lib/repositories";
import { useDilemmasFormStore } from "../../stores/useDilemmaFormStore";
import Uploader from "../upload/uploader";
import { ROUTES } from "@/constants/routes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import * as z from "zod";
import { ButtonGroup } from "@/components/ui/button-group";
import { DILEMMAS_STATUS } from "../../constants";
import { hasStep3Changes } from "../../utils";
import DilemmaScheduler from "../dilemma-scheduler";

const DilemmaFormStepThree = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const step3Data = useDilemmasFormStore((s) => s.step3Data);
  const setStep3Data = useDilemmasFormStore((s) => s.setStep3Data);
  const setCurrentStep = useDilemmasFormStore((s) => s.setCurrentStep);
  const createdDilemma = useDilemmasFormStore((s) => s.createdDilemma);
  const initialDilemma = useDilemmasFormStore((s) => s.initialDilemma);
  const reset = useDilemmasFormStore((s) => s.reset);
  const isEditMode = useDilemmasFormStore((s) => s.isEditMode);
  const reviewComment = useDilemmasFormStore((s) => s.reviewComment);
  const setIsStep3Dirty = useDilemmasFormStore((s) => s.setIsStep3Dirty);
  const [isLoading, setIsLoading] = useState(false);
  const canEdit = !createdDilemma || createdDilemma.can_edit;
  const isMobileSubmission =
    createdDilemma?.source === "mobile" &&
    createdDilemma?.in_submission_process === true;
  const [isDeleting, setIsDeleting] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const links = step3Data.documentationLinks;
  const setLinks = (updated: string[]) =>
    setStep3Data({ documentationLinks: updated });
  const [linkErrors, setLinkErrors] = useState<(string | null)[]>([]);
  const [pastedIndices, setPastedIndices] = useState<Set<number>>(new Set());
  const isSubmission = createdDilemma?.source === "mobile";

  const step3Schema = z.object({
    synthesis: z.string(),
  });

  const form = useForm<z.infer<typeof step3Schema>>({
    resolver: zodResolver(step3Schema),
    defaultValues: { synthesis: step3Data.expertSynthesis },
  });

  const watchedSynthesis = useWatch({
    control: form.control,
    name: "synthesis",
  });

  const hasLocalContentChanges = useMemo(
    () =>
      hasStep3Changes(initialDilemma, {
        synthesis: watchedSynthesis ?? step3Data.expertSynthesis,
        documentationLinks: links,
        uploadedFiles: step3Data.uploadedFiles,
        reviewComment,
      }),
    [
      initialDilemma,
      watchedSynthesis,
      step3Data.expertSynthesis,
      links,
      step3Data.uploadedFiles,
      reviewComment,
    ],
  );
  const effectiveCanPublish =
    (createdDilemma?.can_publish ?? false) && !hasLocalContentChanges;
  const isSubmittedReviewable = isMobileSubmission && !effectiveCanPublish;
  const isSubmittedPublishable = isMobileSubmission && effectiveCanPublish;

  useEffect(() => {
    setIsStep3Dirty(hasLocalContentChanges);
  }, [hasLocalContentChanges, setIsStep3Dirty]);

  useEffect(() => {
    return () => {
      if (!form.formState.isDirty) return;
      setStep3Data({ expertSynthesis: form.getValues("synthesis") ?? "" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handlePublishOnly() {
    if (!createdDilemma) return;
    if (!createdDilemma.can_publish) {
      toast.error(t("modules.dilemmas.errors.incomplete_dilemma"));
      return;
    }
    setIsLoading(true);
    const [error] = await catchError(
      dilemmasRepo.patch(createdDilemma.id, {
        status: DILEMMAS_STATUS.PUBLISHED,
        ...(isEditMode ? { review_comment: reviewComment } : {}),
      }),
    );
    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: ["dilemma", createdDilemma.id],
    });
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    toast.success(t("messages.dilemma_publish_success"));
    reset();
    navigate(isSubmission ? ROUTES.SUBMISSIONS : ROUTES.DILEMMAS);
    setIsLoading(false);
  }

  async function handleReject() {
    if (!createdDilemma) return;
    setIsLoading(true);
    const [error] = await catchError(
      dilemmasRepo.patch(createdDilemma.id, { status: DILEMMAS_STATUS.DRAFT }),
    );
    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    toast.success(t("messages.dilemma_reject_success"));
    reset();
    navigate(ROUTES.SUBMISSIONS);
    setIsLoading(false);
  }

  function handlePrevious() {
    setStep3Data({ expertSynthesis: form.getValues("synthesis") });
    setCurrentStep(2);
  }

  function handleAddLink() {
    setLinks([...links, ""]);
  }

  function handleRemoveLink(index: number) {
    setLinks(links.filter((_, i) => i !== index));
  }

  function stripProtocol(value: string): string {
    return value.replace(/^https?:\/\//i, "");
  }

  function validateLink(value: string): string | null {
    if (!value.trim()) return null;
    try {
      new URL(`https://${value}`);
      return null;
    } catch {
      return t("modules.dilemmas.errors.invalid_url");
    }
  }

  function handleLinkChange(index: number, value: string) {
    let processed = value;
    if (pastedIndices.has(index)) {
      setPastedIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    } else {
      processed = stripProtocol(value);
    }
    const updated = [...links];
    updated[index] = processed;
    setLinks(updated);
    const updatedErrors = [...linkErrors];
    updatedErrors[index] = validateLink(processed);
    setLinkErrors(updatedErrors);
  }

  function handleLinkBlur(index: number) {
    const stripped = stripProtocol(links[index]);
    if (stripped === links[index]) return;
    const updated = [...links];
    updated[index] = stripped;
    setLinks(updated);
    const updatedErrors = [...linkErrors];
    updatedErrors[index] = validateLink(stripped);
    setLinkErrors(updatedErrors);
  }

  function handleCopyLink(value: string) {
    navigator.clipboard.writeText(`https://${value}`);
  }

  async function handleDeleteFile() {
    if (!fileToDelete || !createdDilemma) return;

    setIsDeleting(true);
    const [error] = await catchError(dilemmasRepo.deleteFile(fileToDelete));

    if (error) {
      toast.error(handleApiError(error).message);
      setIsDeleting(false);
      setFileToDelete(null);
      return;
    }

    setStep3Data({
      uploadedFiles: step3Data.uploadedFiles.filter(
        (f) => f.id !== fileToDelete,
      ),
    });
    setIsDeleting(false);
    setFileToDelete(null);
    toast.success(t("messages.file_delete_success"));
  }

  async function handleSchedule(scheduledDate: string) {
    if (!createdDilemma) return;

    if (!createdDilemma.can_publish) {
      toast.error(t("modules.dilemmas.errors.incomplete_dilemma"));
      return;
    }

    setIsLoading(true);

    const [error] = await catchError(
      dilemmasRepo.patch(createdDilemma.id, {
        status: DILEMMAS_STATUS.SCHEDULED,
        scheduled_for_date: scheduledDate,
        ...(isEditMode ? { review_comment: reviewComment } : {}),
      }),
    );

    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["dilemmas"],
    });
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    toast.success(t("messages.dilemma_schedule_success"));
    reset();
    navigate(isSubmission ? ROUTES.SUBMISSIONS : ROUTES.DILEMMAS);
    setIsLoading(false);
  }

  async function onSubmit(data: z.infer<typeof step3Schema>, status: string) {
    if (!createdDilemma) return;

    setIsLoading(true);

    const filteredLinks = links
      .filter((l) => l.trim() !== "")
      .map((l) => `https://${l}`);

    const [error] = await catchError(
      dilemmasRepo.patch(
        createdDilemma.id,
        {
          synthesis: data.synthesis,
          documentation_links: filteredLinks,
          status,
          ...(isEditMode ? { review_comment: reviewComment } : {}),
        },
        { is_last_step: true },
      ),
    );

    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["dilemma", createdDilemma.id],
    });
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    toast.success(
      t(
        isEditMode
          ? "messages.dilemma_edit_success"
          : "messages.dilemma_create_success",
      ),
    );
    reset();
    navigate(isSubmission ? ROUTES.SUBMISSIONS : ROUTES.DILEMMAS);
    setIsLoading(false);
  }

  function handleSave(status: string) {
    if (
      status === DILEMMAS_STATUS.PUBLISHED &&
      createdDilemma &&
      !createdDilemma.can_publish
    ) {
      toast.error(t("modules.dilemmas.errors.incomplete_dilemma"));
      return;
    }

    const errors = links.map((link) => validateLink(link));
    setLinkErrors(errors);

    const hasLinkErrors = errors.some((e) => e !== null);
    if (hasLinkErrors) return;

    form.handleSubmit(
      (data) => onSubmit(data, status),
      () => toast.error(t("modules.dilemmas.errors.synthesis_required")),
    )();
  }

  return (
    <div className="flex flex-col gap-4">
      <form id="dilemma-form-step-3">
        <FieldGroup className="gap-4">
          {/* Synthesis */}
          <Controller
            name="synthesis"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="synthesis-field">
                  {t("modules.dilemmas.form.general_comment")}
                </FieldLabel>
                <Textarea
                  {...field}
                  id="synthesis-field"
                  rows={5}
                  aria-invalid={fieldState.invalid}
                  disabled={!canEdit}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
        </FieldGroup>
      </form>

      {/* File upload */}
      {createdDilemma && (
        <Uploader
          dilemmaId={createdDilemma.id}
          uploadedFiles={step3Data.uploadedFiles}
          onUploadComplete={(file) =>
            setStep3Data({
              uploadedFiles: [...step3Data.uploadedFiles, file],
            })
          }
          onRemove={(fileId) =>
            setStep3Data({
              uploadedFiles: step3Data.uploadedFiles.filter(
                (f) => f.id !== fileId,
              ),
            })
          }
          isEditMode={isEditMode}
          onDelete={(fileId) => setFileToDelete(fileId)}
          isDeleting={isDeleting}
        />
      )}

      {/* Delete file confirmation dialog */}
      {fileToDelete && (
        <DeleteConfirmDialog
          open={!!fileToDelete}
          onOpenChange={(open) => {
            if (!open) setFileToDelete(null);
          }}
          onConfirm={handleDeleteFile}
          isLoading={isDeleting}
        />
      )}

      {/* External links */}
      <div className="flex flex-col gap-3">
        {links.map((link, index) => (
          <div key={index} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div
                className={`flex items-center border flex-1 px-2 py-1 rounded-md ${linkErrors[index] ? "border-destructive" : ""}`}
              >
                <span className="text-sm text-muted-foreground shrink-0 select-none">
                  https://
                </span>
                <Input
                  value={link}
                  onChange={(e) => handleLinkChange(index, e.target.value)}
                  onBlur={() => handleLinkBlur(index)}
                  onPaste={() =>
                    setPastedIndices((prev) => new Set(prev).add(index))
                  }
                  placeholder="example.com/path"
                  aria-invalid={!!linkErrors[index]}
                  disabled={!canEdit}
                  className="flex-1 bg-white border-0 ring-0 focus:ring-0 focus-visible:ring-0 file:border-0 aria-invalid:border-0 pl-1"
                />
                {link.trim() && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-indigo-500"
                    onClick={() => handleCopyLink(link)}
                    size="icon"
                  >
                    <Copy className="size-4" />
                  </Button>
                )}
              </div>
              <Button
                type="button"
                variant="primary_light"
                onClick={() => handleRemoveLink(index)}
                size="icon"
                disabled={!canEdit}
              >
                <X className="size-4" />
              </Button>
              {index === links.length - 1 ? (
                <Button
                  type="button"
                  className="bg-indigo-50 hover:bg-indigo-100 text-indigo-500"
                  onClick={handleAddLink}
                  size="icon"
                  disabled={!canEdit}
                >
                  <CirclePlus className="size-4" />
                </Button>
              ) : null}
            </div>
            {linkErrors[index] && (
              <p className="text-sm text-destructive">{linkErrors[index]}</p>
            )}
          </div>
        ))}

        {links.length === 0 ? (
          <Button
            type="button"
            size="lg"
            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-500"
            onClick={handleAddLink}
            disabled={!canEdit}
          >
            {t("modules.dilemmas.form.add_external_link")}
            <CirclePlus className="size-4" />
          </Button>
        ) : null}
      </div>

      {/* Navigation */}
      <BottomFixed className="bg-white">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="border-primary border text-primary"
            disabled={isLoading}
            type="button"
            onClick={handlePrevious}
          >
            <ArrowLeft />
            <span className="hidden lg:inline">{t("keys.previous")}</span>
          </Button>
          {isSubmittedReviewable ? (
            <div className="flex items-center gap-3">
              <Button
                variant="primary_light"
                disabled={isLoading}
                type="button"
                onClick={handleReject}
              >
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  <Undo className="size-4" />
                )}
                {t("keys.reject_dilemma")}
              </Button>
              <Button
                disabled={isLoading || !canEdit}
                type="button"
                onClick={() => handleSave(createdDilemma.status)}
              >
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                {t("keys.save")}
              </Button>
            </div>
          ) : isSubmittedPublishable ? (
            <div className="flex items-center gap-3">
              <DilemmaScheduler
                isLoading={isLoading}
                onConfirm={handleSchedule}
                disabled={!canEdit}
                initialDate={createdDilemma?.scheduled_for_date}
              />
              <Button
                disabled={isLoading || !canEdit}
                type="button"
                variant="ghost"
                className="border border-primary text-primary"
                onClick={() => handleSave(createdDilemma.status)}
              >
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                {t("keys.save")}
              </Button>
              <Button
                disabled={isLoading || !canEdit}
                type="button"
                onClick={handlePublishOnly}
              >
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                {t("modules.dilemmas.keys.publish_action")}
              </Button>
            </div>
          ) : (
            <>
              {/* Dropdown version: visible below xl */}
              <ButtonGroup className="xl:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-white text-primary border hover:bg-gray-50">
                      <Ellipsis />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuGroup className="space-y-1">
                      <DropdownMenuItem asChild>
                        <DilemmaScheduler
                          isLoading={isLoading}
                          onConfirm={handleSchedule}
                          disabled={
                            !canEdit ||
                            (createdDilemma?.status !== DILEMMAS_STATUS.DRAFT &&
                              createdDilemma?.status !==
                                DILEMMAS_STATUS.SCHEDULED)
                          }
                          initialDate={createdDilemma?.scheduled_for_date}
                        />
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Button
                          disabled={isLoading || !canEdit}
                          type="button"
                          variant="secondary"
                          className="w-full text-gray-600 justify-start hover:text-gray-600 cursor-pointer"
                          onClick={() => handleSave(DILEMMAS_STATUS.DRAFT)}
                        >
                          {isLoading ? (
                            <Loader className="animate-spin" />
                          ) : (
                            <Bookmark className="size-4 text-gray-600" />
                          )}
                          {t("keys.save_as_draft")}
                        </Button>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  disabled={isLoading || !canEdit}
                  type="button"
                  onClick={() => handleSave(DILEMMAS_STATUS.PUBLISHED)}
                >
                  {isLoading ? (
                    <Loader className="animate-spin" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  {t("keys.save_and_publish")}
                </Button>
              </ButtonGroup>

              {/* Inline version: visible at xl+ */}
              <ButtonGroup className="hidden xl:flex border rounded-md">
                <DilemmaScheduler
                  isLoading={isLoading}
                  onConfirm={handleSchedule}
                  disabled={
                    !canEdit ||
                    (createdDilemma?.status !== DILEMMAS_STATUS.DRAFT &&
                      createdDilemma?.status !== DILEMMAS_STATUS.SCHEDULED)
                  }
                  initialDate={createdDilemma?.scheduled_for_date}
                />
                <Button
                  disabled={isLoading || !canEdit}
                  type="button"
                  variant="ghost"
                  onClick={() => handleSave(DILEMMAS_STATUS.DRAFT)}
                >
                  {isLoading ? (
                    <Loader className="animate-spin" />
                  ) : (
                    <Bookmark className="size-4" />
                  )}
                  {t("keys.save_as_draft")}
                </Button>
                <Button
                  disabled={isLoading || !canEdit}
                  type="button"
                  onClick={() => handleSave(DILEMMAS_STATUS.PUBLISHED)}
                >
                  {isLoading ? (
                    <Loader className="animate-spin" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  {t("keys.save_and_publish")}
                </Button>
              </ButtonGroup>
            </>
          )}
        </div>
      </BottomFixed>
    </div>
  );
};

function DeleteConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{t("keys.confirm_delete.title")}</DialogTitle>
          <DialogDescription>
            {t("keys.confirm_delete.description")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              {t("keys.cancel")}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader className="animate-spin" />}
            {t("keys.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DilemmaFormStepThree;
